'use client';

import { useEffect, useState } from 'react';
import { ChevronRight, Receipt } from 'lucide-react';
import { SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { Invoice } from '@/lib/types';
import { isUnauthorized } from '@/lib/api';
import { fmtMoney, fmtShortDate } from '@/lib/format';
import { useProfile } from './ProfileContext';
import { Bar, CARD, CTA_SMALL, DISPLAY, FactCard, Kicker, PageBody, Pii, ProfileSheet } from './ui';

/** Invoice_Statuses 7 = Cancelled: MP leaves the balance on the row, but nobody owes it. */
const CANCELLED = 7;
const isCancelled = (inv: Invoice) => inv.statusId === CANCELLED;
const isOpen = (inv: Invoice) => inv.balance > 0 && !isCancelled(inv) && !isRefunded(inv);
/** Everything paid went back. Owes nothing, whatever the balance says on an older API. */
const isRefunded = (inv: Invoice) => (inv.refunded ?? 0) > 0 && (inv.refunded ?? 0) >= inv.paid;

/**
 * Registrations as one card: what is still owed said big (or "All paid
 * up"), how many invoices, the next one due. Tap anywhere and the sheet
 * lists every invoice: still owed, paid, cancelled. Paying happens on /pay/.
 * Hidden when there are none.
 */
export function InvoicesCard() {
  const { api, urls } = useProfile();
  const [invoices, setInvoices] = useState<Invoice[] | null | 'error'>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getInvoices(controller.signal)
      .then((i) => {
        if (!controller.signal.aborted) setInvoices(i);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setInvoices(isUnauthorized(err) ? [] : 'error');
      });
    return () => controller.abort();
  }, [api]);

  if (invoices !== null && invoices !== 'error' && invoices.length === 0) return null;

  const list = invoices && invoices !== 'error' ? invoices : [];
  const owed = list.filter(isOpen);
  const past = list.filter((i) => !isOpen(i) && !isCancelled(i));
  const cancelled = list.filter(isCancelled);
  const due = owed.reduce((sum, i) => sum + i.balance, 0);
  const next = owed[0];

  return (
    <>
      <FactCard
        title="Invoices"
        icon={<Receipt className="h-4 w-4" strokeWidth={2.5} />}
        onEdit={() => setOpen(true)}
        editLabel="View"
        className={owed.length > 0 ? 'ring-2 ring-amber-300' : ''}
      >
        {invoices === null ? (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Bar className="h-10 w-32" />
            <Bar className="h-3 w-40" />
          </div>
        ) : invoices === 'error' ? (
          <p className="text-sm text-gray-500">We couldn't load your invoices right now.</p>
        ) : (
          <>
            <p className={`${DISPLAY} text-4xl leading-none text-[#1c2b39]`}>
              {owed.length > 0 ? (
                <>
                  <Pii>{fmtMoney(due)}</Pii> due
                </>
              ) : (
                'All paid up'
              )}
            </p>
            <p className="text-sm text-gray-500">
              {list.length} {list.length === 1 ? 'invoice' : 'invoices'}
              {next ? (
                <>
                  {' '}
                  · next:{' '}
                  <span className="font-semibold text-[#1c2b39]">{next.lines[0]?.item}</span>
                </>
              ) : past[0] ? (
                <>
                  {' '}
                  · last:{' '}
                  <span className="font-semibold text-[#1c2b39]">{past[0].lines[0]?.item}</span>
                </>
              ) : null}
            </p>
            {next && (
              <a
                href={next.payUrl ?? `${urls.pay}?invoiceId=${next.invoiceId}`}
                onClick={(e) => e.stopPropagation()}
                className={`${CTA_SMALL} mt-auto self-start bg-[#299c8f] text-white`}
              >
                Pay <Pii>{fmtMoney(next.balance)}</Pii>
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            )}
          </>
        )}
      </FactCard>

      <ProfileSheet open={open} onClose={() => setOpen(false)} title="Invoices">
        <SheetPage name="main">
          <PageBody>
            {owed.length > 0 && (
              <div className="flex flex-col gap-2">
                <Kicker>Still owed</Kicker>
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2">
                  {owed.map((inv) => (
                    <InvoiceCard key={inv.invoiceId} inv={inv} payBase={urls.pay} />
                  ))}
                </div>
              </div>
            )}
            {past.length > 0 && (
              <div className="flex flex-col gap-2">
                <Kicker>Paid</Kicker>
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2">
                  {past.map((inv) => (
                    <InvoiceCard key={inv.invoiceId} inv={inv} payBase={urls.pay} />
                  ))}
                </div>
              </div>
            )}
            {cancelled.length > 0 && (
              <div className="flex flex-col gap-2">
                <Kicker>Cancelled</Kicker>
                <div className="grid grid-cols-1 gap-3 @xl:grid-cols-2">
                  {cancelled.map((inv) => (
                    <InvoiceCard key={inv.invoiceId} inv={inv} payBase={urls.pay} />
                  ))}
                </div>
              </div>
            )}
          </PageBody>
        </SheetPage>
      </ProfileSheet>
    </>
  );
}

function InvoiceCard({
  inv,
  payBase,
  className = '',
}: {
  inv: Invoice;
  payBase: string;
  className?: string;
}) {
  const open = isOpen(inv);
  const cancelled = isCancelled(inv);
  const refunded = isRefunded(inv);
  const pay = open ? (inv.payUrl ?? `${payBase}?invoiceId=${inv.invoiceId}`) : null;
  return (
    <div
      className={`${CARD} flex flex-col gap-3 p-4 ${open ? 'ring-2 ring-amber-300' : ''} ${className}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0">
          <span className={`${DISPLAY} block text-2xl leading-none text-[#1c2b39]`}>
            {open ? (
              <>
                <Pii>{fmtMoney(inv.balance)}</Pii> due
              </>
            ) : (
              <Pii>{fmtMoney(inv.total)}</Pii>
            )}
          </span>
          <span className="block text-xs text-gray-500">
            Invoice {inv.invoiceId} · {fmtShortDate(inv.date)}
          </span>
        </span>
        <span
          className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase ${
            open
              ? 'bg-amber-100 text-amber-800'
              : cancelled
                ? 'bg-gray-100 text-gray-500'
                : refunded
                  ? 'bg-slate-200 text-slate-700'
                  : 'bg-[#299c8f] text-white'
          }`}
        >
          {open ? inv.status : cancelled ? 'Cancelled' : refunded ? 'Refunded' : 'Paid'}
        </span>
      </div>
      <div className="divide-y divide-gray-100 rounded-2xl bg-gray-50 px-3">
        {inv.lines.map((line, i) => (
          <div key={i} className="flex items-start gap-3 py-2">
            <span className="min-w-0 flex-1 text-sm text-[#1c2b39]">
              <span className="line-clamp-1 font-semibold">{line.item}</span>
              {line.recipient && (
                <span className="pii block truncate text-xs text-gray-500">
                  for {line.recipient}
                </span>
              )}
            </span>
            <span className="shrink-0 text-sm text-[#1c2b39] tabular-nums">
              <Pii>{fmtMoney(line.amount)}</Pii>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-auto flex flex-wrap items-center justify-between gap-3 text-sm">
        <span className="text-gray-500">
          <Pii>{fmtMoney(inv.paid)}</Pii> paid of <Pii>{fmtMoney(inv.total)}</Pii>
          {(inv.refunded ?? 0) > 0 && (
            <>
              {' · '}
              <Pii>{fmtMoney(inv.refunded ?? 0)}</Pii> refunded
            </>
          )}
        </span>
        {pay && (
          <a href={pay} className={`${CTA_SMALL} bg-[#299c8f] text-white`}>
            Pay <Pii>{fmtMoney(inv.balance)}</Pii>
            <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
          </a>
        )}
      </div>
    </div>
  );
}
